import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import PdfPreview from '../../../../Common/PDFPreview';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { RingLoader } from 'react-spinners';
import PrintButton from "../../../../Common/Buttons/PrintPDF";
import { formatReadableAmount } from "../../../../Common/FormatFunctions/FormatAmount"
import {
    Box,
    Typography,
    Button,
    Stack,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import SearchBar from "../../../../Common/UtilityCommon/SearchBar";

const API_URL = process.env.REACT_APP_API;

const MillWiseLiftingWise = () => {

    const Company_Name = sessionStorage.getItem("Company_Name")
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')

    const [groupedData, setGroupedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pdfPreview, setPdfPreview] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${API_URL}/millwise-stock-report`, {
                    params: { Company_Code: sessionStorage.getItem('Company_Code') },
                });
                const data = response.data;
                filterSelfRecords(data);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const filterSelfRecords = (data) => {
        const grouped = data.tender_details.map((tender) => {
            const salesDetails = data.sales_details.find(
                (sale) => sale.Tender_No === tender.Tender_No
            );

            const validDetails = salesDetails
                ? salesDetails.details.filter((sale) => parseFloat(sale.BALANCE) !== 0)
                : [];

            return {
                ...tender.details[0],
                salesDetails: validDetails,
            };
        });

        setGroupedData(grouped.filter((tender) => tender.salesDetails.length > 0));
    };

    const handleExportToExcel = () => {
        const table = document.getElementById('reportContent').querySelector('table');
        const rows = Array.from(table.rows);
        const sheetData = [];

        rows.forEach((row) => {
            const rowData = Array.from(row.cells).map((cell) => cell.textContent.trim());
            sheetData.push(rowData);
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'MillWiseLiftingWise');
        XLSX.writeFile(workbook, 'MillWiseLiftingWise.xlsx');
    };

    const generatePDF = () => {
        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        let yOffset = 10;

        const ensurePageSpace = (doc, yOffset, requiredSpace = 60) => {
            const pageHeight = doc.internal.pageSize.getHeight();
            if (yOffset + requiredSpace > pageHeight) {
                doc.addPage();
                return 10;
            }
            return yOffset;
        };

        // Report Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 102);
        doc.text(Company_Name, pageWidth / 2, yOffset, { align: 'center' });

        yOffset += 8;
        doc.setFontSize(11);
        doc.text(`GSTN: ${Company_GSTNO}`, pageWidth / 2, yOffset, { align: 'center' });

        yOffset += 8;
        doc.setFontSize(13);
        doc.text("Millwise Lifting Stock Report", pageWidth / 2, yOffset, { align: 'center' });

        yOffset += 6;
        const dateText = `Generated on: ${new Date().toLocaleString()}`;
        doc.setFontSize(9);
        doc.text(dateText, pageWidth / 2, yOffset, { align: 'center' });

        yOffset += 10;

        const millGroups = {};
        groupedData.forEach(tender => {
            const mill = tender.millname || 'Unknown Mill';
            if (!millGroups[mill]) millGroups[mill] = [];
            millGroups[mill].push(tender);
        });

        Object.entries(millGroups).forEach(([millName, tenders]) => {
            yOffset = ensurePageSpace(doc, yOffset, 30);

            // Mill Heading Block
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(255);
            doc.setFillColor(0, 51, 153); // blue
            doc.rect(10, yOffset, pageWidth - 20, 8, 'F');
            doc.text(`Mill: ${millName}`, 12, yOffset + 5);
            yOffset += 10;

            tenders.forEach((tender, index) => {
                const totalDispatch = tender.salesDetails.reduce((sum, s) => sum + parseFloat(s.despatchqty || 0), 0);
                const totalBalance = tender.salesDetails.reduce((sum, s) => sum + parseFloat(s.BALANCE || 0), 0);

                const tenderRow = [[
                    tender.Tender_No,
                    tender.Tender_Date || '',
                    tender.Grade || '',
                    '', // Lot
                    formatReadableAmount(tender.Mill_Rate),
                    formatReadableAmount(tender.Purc_Rate),
                    formatReadableAmount(tender.Quantal),
                    formatReadableAmount(totalDispatch),
                    formatReadableAmount(totalBalance),
                    '',
                    tender.Lifting_Date || '',
                    tender.doname || ''
                ]];

                const buyerRows = tender.salesDetails.map((sale) => [
                    `${sale.ID} - ${sale.buyerbrokerfullname || ''}`,
                    '', sale.Grade, '', sale.MillRate,
                    formatReadableAmount(sale.Sale_Rate),
                    formatReadableAmount(sale.Buyer_Quantal),
                    formatReadableAmount(sale.despatchqty),
                    formatReadableAmount(sale.BALANCE),
                    sale.Sauda_Date, '', ''
                ]);

                const allRows = [...tenderRow, ...buyerRows];

                doc.autoTable({
                    startY: yOffset,
                    head: [[
                        'Tender No.', 'Date', 'Grade', 'Lot', 'Mill Rate',
                        'Sale Rate', 'Quintal', 'Dispatched', 'Balance', 'Sauda', 'Lifting', 'DO'
                    ]],
                    body: allRows,
                    theme: 'grid',
                    styles: {
                        fontSize: 8,
                        overflow: 'linebreak',
                        cellPadding: 1.5,
                    },
                    headStyles: {
                        fillColor: [245, 245, 245],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                    },
                    columnStyles: {
                        0: { cellWidth: 40 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 18 },
                        4: { cellWidth: 22 },
                        5: { cellWidth: 22 },
                        6: { cellWidth: 22 },
                        7: { cellWidth: 20 },
                        8: { cellWidth: 20 },
                        9: { cellWidth: 17 },
                        10: { cellWidth: 17 },
                        11: { cellWidth: 29 },
                    },
                    didParseCell: function (data) {
                        if (data.row.index === 0) {
                            data.cell.styles.fillColor = index % 2 === 0 ? [223, 240, 216] : [242, 242, 242];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (data.column.index === 0) {
                            data.cell.styles.textColor = [0, 0, 200];
                        }
                    },
                    margin: { left: 10, right: 10 },
                });

                yOffset = doc.lastAutoTable.finalY + 5;

                if (yOffset > 190) {
                    doc.addPage();
                    yOffset = 10;
                }
            });
        });

        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        setPdfPreview(url);
    };


    const filteredData = groupedData.filter((tender) => {
        const query = searchQuery.toLowerCase();

        const tenderMatch = Object.values(tender).some(val =>
            String(val).toLowerCase().includes(query)
        );

        const salesMatch = tender.salesDetails.some(sale =>
            Object.values(sale).some(val =>
                String(val).toLowerCase().includes(query)
            )
        );

        return tenderMatch || salesMatch;
    });


    // Hadle tender settlement
    const handleSettle = (tenderNo) => {
        console.log(`Settling Tender: ${tenderNo}`);

    };

    // Handle sale settlement
    const handleSettleSale = (saleID) => {
        console.log(`Settling Sale: ${saleID}`);
    };

    return (
        <Box style={{ marginTop: "-90px" }}>
            <Typography variant="h6" align="center" fontWeight="bold" color="blue" fontSize="16px">
                Mill Wise Stock Report
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box sx={{ flex: 1 }} />

                <Box sx={{ flexShrink: 0 }}>
                    <Box style={{ minWidth: "250px", maxWidth: "600px", margin: "0 auto" }}>
                        <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    </Box>
                </Box>

                {/* Right-aligned Buttons */}
                <Stack direction="row" spacing={2} sx={{ flex: 1 }} justifyContent="flex-end">
                    {pdfPreview && (
                        <PdfPreview pdfData={pdfPreview} apiData={groupedData} label="Millwise Lifting Stock Report" />
                    )}
                    <PrintButton disabledFeild={""} fetchData={generatePDF} />
                    <Button variant="outlined" color="secondary" onClick={handleExportToExcel}>
                        Export to Excel
                    </Button>
                </Stack>
            </Stack>

            <Box sx={{
                height: 'calc(100vh - 150px)',
                overflow: 'auto',
                position: 'relative',
                border: 'dashed rgb(134, 133, 133)',
                borderRadius: '4px'
            }}>
                <TableContainer
                    id="reportContent"
                    component={Paper}
                    sx={{
                        position: 'relative',
                        overflow: 'auto',
                        height: '100%'
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            minWidth: 1000,
                            '& .MuiTableCell-root': {
                                border: '0.1px dashed rgb(134, 133, 133)'
                            },
                            '& .MuiTableCell-head': {
                                backgroundColor: 'black',
                                color: 'white',
                                position: 'sticky',
                                top: 0,
                                zIndex: 2
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {[
                                    'Tender No.', 'Date', 'Mill', 'Grade', 'Lot', 'Mill Rate',
                                    'Sale Rate', 'Quintal', 'Dispatched', 'Balance', 'Lifting Date', 'Sauda Date', 'DO'
                                ].map((header) => (
                                    <TableCell
                                        key={header}
                                        align="center"
                                        sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((tender, index) => {
                                const totalDispatch = tender.salesDetails.reduce(
                                    (sum, sale) => sum + parseFloat(sale.despatchqty || 0), 0);
                                const totalBalance = tender.salesDetails.reduce(
                                    (sum, sale) => sum + parseFloat(sale.BALANCE || 0), 0);

                                const saleWithID1 = tender.salesDetails.find(sale => sale.ID === 1);
                                const qtyDifference = saleWithID1
                                    ? Number(tender.Quantal || 0) - Number(saleWithID1.Buyer_Quantal || 0)
                                    : Number(tender.Quantal || 0) - Number(tender.Buyer_Quantal || 0);

                                return (
                                    <React.Fragment key={index}>
                                        <TableRow sx={{
                                            fontWeight: 'bold'
                                        }}>
                                            <TableCell rowSpan={tender.salesDetails.length + 1} sx={{ textAlign: 'left', fontSize: "16px", fontWeight: "bold" }}>
                                                {tender.Tender_No}
                                            </TableCell>
                                            <TableCell style={{ whiteSpace: "nowrap" }}>
                                                {tender.Tender_Date} / <span style={{ color: 'red' }}> Dispatched - {Number(tender.Quantal || 0) - Number(totalBalance || 0)} </span> /
                                                {qtyDifference !== null ? (
                                                    <span style={{ color: 'green', fontWeight: 'bold' }}>
                                                        Sauda - {qtyDifference}
                                                    </span>
                                                ) : ''}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'left', fontSize: "16px", fontWeight: "bold" }}>{tender.millname}</TableCell>
                                            <TableCell style={{ whiteSpace: "nowrap" }}>{tender.Grade}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatReadableAmount(tender.Quantal)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatReadableAmount(tender.Mill_Rate)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatReadableAmount(tender.Purc_Rate)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatReadableAmount(tender.Quantal)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatReadableAmount(totalDispatch)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                {formatReadableAmount(totalBalance)}
                                            </TableCell>
                                            <TableCell>{tender.Lifting_Date}</TableCell>
                                            <TableCell>{tender.Sauda_Date}</TableCell>
                                            <TableCell>{tender.doname}</TableCell>
                                            <TableCell>
                                            </TableCell>
                                        </TableRow>

                                        {tender.salesDetails.map((sale, saleIndex) => (
                                            <TableRow key={saleIndex} sx={{
                                                backgroundColor: sale.ID === 1 ? '#ffcccc' : 'white'
                                            }}>
                                                <TableCell sx={{ color: 'blue' }}>
                                                    {sale.ID}-{sale.buyerbrokerfullname}
                                                </TableCell>
                                                <TableCell />
                                                <TableCell >{sale.Grade}</TableCell>
                                                <TableCell />
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    {formatReadableAmount(sale.MillRate)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    {formatReadableAmount(sale.Sale_Rate)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    {formatReadableAmount(sale.Buyer_Quantal)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    {formatReadableAmount(sale.despatchqty)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    {formatReadableAmount(sale.BALANCE)}
                                                </TableCell>
                                                <TableCell />
                                                <TableCell>{sale.Sauda_Date}</TableCell>
                                                {/* Add the "Done" column for sales details */}
                                                {/* <TableCell>
                                                {sale.ID !== 1 && (
                                                    <Button variant="outlined" color="primary" onClick={() => handleSettleSale(sale.ID)}>
                                                        Settle
                                                    </Button>
                                                )}
                                            </TableCell> */}
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={14} sx={{ height: 30, backgroundColor: '#f5f5f5' }} />
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                        {loading && (
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                zIndex: 3
                            }}>
                                <RingLoader />
                            </div>
                        )}
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );

};

export default MillWiseLiftingWise;
